import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _uniqId from 'lodash/uniqueId';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import I18n from 'i18n-js';
import { Row, Col, Button, Icon, Select, Drawer } from 'grab-ui';
import RouteOptimizationLocationDrawer from 'components/v2/route-optimization-location-drawer/route-optimization-location-drawer';
import { getCompanyID, getUserSelectedCityCountryCode } from 'helper/user';
import { Mobile } from 'components/v2/responsive';

import { setExtraFields, validRecipientLocations } from 'helper/order';
import { getFormattedAmount } from 'helper/quote';
import { FIELD_NAME } from 'views/v2/home/constants';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_HOME_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';

import selectFieldCss from 'components/v2/select-field/select-field.less';
import css from './book-order-widget.less';

const SelectOption = Select.Option;

class BookOrderWidget extends Component {
  constructor(props) {
    super(props);
    this.state = {
      locationDrawerID: null,
      onUpdateLocation: () => {},
      allowMultipleAddresses: false,
      swapDisabled: false,
    };
  }

  onClickPickupLocation = () => {
    const { order, toggleLocationDrawer, userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, 'PICKUP_ADDRESS', {
      [custom.COMPANY_ID]: companyID,
      PICKUP_POI: _get(order, 'senderLocation.id'),
    });

    toggleLocationDrawer(
      _isEmpty(order.senderLocation) ? [] : [order.senderLocation],
      true,
    );
    this.setState({
      locationDrawerID: _uniqId('drawer'),
      onUpdateLocation: this.onUpdatePickupLocation,
      allowMultipleAddresses: false,
    });
  };

  onClickDropoffLocation = () => {
    const { order, toggleLocationDrawer, userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, 'DROPOFF_ADDRESS', {
      [custom.COMPANY_ID]: companyID,
      DROPOFF_POI: _get(order, 'recipientLocations[0].id'),
    });

    toggleLocationDrawer(
      _isEmpty(order.recipientLocations) ? [] : order.recipientLocations,
      false,
      true,
    );
    this.setState({
      locationDrawerID: _uniqId('drawer'),
      onUpdateLocation: this.onUpdateDropoffLocation,
      allowMultipleAddresses: true,
    });
  };

  onClickSwapLocation = e => {
    if (e) {
      e.stopPropagation(); // prevent click event to show location drawer
    }
    if (this.state.swapDisabled) return;

    const { order, updateOrder, userInfo } = this.props;

    const companyID = getCompanyID(userInfo);
    trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, 'SWAP', {
      [custom.COMPANY_ID]: companyID,
      PICKUP_POI: _get(order, 'senderLocation.id'),
      DROPOFF_POI: _get(order, 'recipientLocations[0].id'),
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
      eventName: SCRIBE_HOME_EVENTS.SWAP,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: _get(order, 'senderLocation.id'),
        [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: _get(
          order,
          'recipientLocations[0].id',
        ),
      },
    });

    const senderLocation = _get(order, 'senderLocation');
    const recipientLocations = _get(order, 'recipientLocations', []);
    const newOrder = {
      ...order,
      recipientLocations: _isEmpty(senderLocation) ? [] : [senderLocation],
      senderLocation: recipientLocations[0] || {},
    };
    updateOrder(newOrder);
  };

  onUpdatePickupLocation = locations => {
    const { order, updateOrder, userInfo } = this.props;
    const newOrder = {
      ...order,
      senderLocation: locations[0],
    };
    updateOrder(newOrder);

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
      eventName: SCRIBE_HOME_EVENTS.PICKUP_ADDRESS,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: locations[0].id,
      },
    });
  };

  onUpdateDropoffLocation = locations => {
    const { order, updateOrder, userInfo } = this.props;
    const newOrder = {
      ...order,
      recipientLocations: locations,
    };
    setExtraFields(newOrder);
    updateOrder(newOrder);
    this.setSwapDisabled();

    if (locations.length > 0) {
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
        eventName: SCRIBE_HOME_EVENTS.DROPOFF_ADDRESS,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: locations.map(loc => loc.id),
        },
      });
    }
  };

  onChangeServiceType = serviceTypeID => {
    const { order, updateOrder, userInfo, serviceTypes } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, 'SERVICE_TYPE', {
      [custom.COMPANY_ID]: companyID,
      SERVICE_TYPE: serviceTypeID,
    });

    const serviceTypesNames = serviceTypes.map(st => st.name);
    const serviceTypesIDs = serviceTypes.map(st => st.ID);
    const selectedServiceType = serviceTypes.find(
      st => st.ID.toString() === serviceTypeID,
    );

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
      eventName: SCRIBE_HOME_EVENTS.SERVICE_TYPE,
      facets: {
        ...getCommonFacet(userInfo),
        // TODO: Are there better event param names?
        [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: selectedServiceType.name,
        [SCRIBE_EVENT_PARAMS.SERVICE_TYPE_LIST]: serviceTypesNames,
        [SCRIBE_EVENT_PARAMS.TAXI_TYPE]: serviceTypeID,
        [SCRIBE_EVENT_PARAMS.TAXI_TYPE_LIST]: serviceTypesIDs,
      },
    });

    const newOrder = {
      ...order,
      [FIELD_NAME.SERVICE_TYPE]: {
        name: FIELD_NAME.SERVICE_TYPE,
        value: serviceTypeID,
      },
    };
    updateOrder(newOrder);
  };

  setSwapDisabled = () => {
    const { order: { recipientLocations } } = this.props;
    this.setState({
      swapDisabled: recipientLocations && recipientLocations.length >= 1,
    });
  };

  buildPickupLocation = () => {
    const { order } = this.props;
    return _get(
      order,
      'senderLocation.address.name',
      I18n.t('v2.homepage.manualOrder.placeholder.senderLocation'),
    );
  };

  buildDropoffLocations = () => {
    const { order } = this.props;
    const recipientLocations = validRecipientLocations(order);
    const dropoffs = recipientLocations ? recipientLocations.length : 0;
    switch (dropoffs) {
      case 0:
        return I18n.t('v2.homepage.manualOrder.placeholder.recipientLocation');
      case 1:
        return recipientLocations[0].address.name;
      default:
        return I18n.t(
          'v2.homepage.manualOrder.placeholder.recipientLocationAndMore',
          {
            firstAddress: recipientLocations[0].address.name,
            remainingCount: dropoffs - 1,
          },
        );
    }
  };

  render() {
    const {
      order,
      userInfo,
      serviceTypes,
      onClickReviewOrder,
      locationDrawer,
    } = this.props;

    const {
      locationDrawerID,
      onUpdateLocation,
      allowMultipleAddresses,
      swapDisabled,
    } = this.state;

    const showOrderWidget = !_isEmpty(serviceTypes);
    if (!showOrderWidget) {
      return (
        <Row className={css.bookOrderWidgetNoService} type="flex">
          <img src={require('public/images/no-available-service.svg')} alt="" />
          <span>Delivery is not available in this city</span>
        </Row>
      );
    }
    const hideServiceTypeSelect = showOrderWidget && serviceTypes.length < 2;

    const quoteForRender = _get(order, 'quote.forRender', {});
    const displayPrice = getFormattedAmount(
      quoteForRender,
      getUserSelectedCityCountryCode(userInfo),
    );
    const disableReviewButton =
      order.fetchingQuote ||
      _isEmpty(quoteForRender) ||
      !_isEmpty(quoteForRender.errors);

    const selectedServiceTypeID =
      _get(order[FIELD_NAME.SERVICE_TYPE], 'value') ||
      _get(serviceTypes, '[0].ID', '').toString();
    const selectedServiceType = serviceTypes.find(
      st => st.ID.toString() === selectedServiceTypeID,
    );
    const maxMSDStops = _get(selectedServiceType, 'maxMSDStops', 1);
    const currentStops = _get(order, 'recipientLocations', []).length;

    return (
      <Row className={css.bookOrderWidget} type="flex">
        <Col
          xs={24}
          md={hideServiceTypeSelect ? 10 : 7}
          className={css.pickupDropoffLocation}
          onClick={this.onClickPickupLocation}
        >
          <div className={css.location}>
            <img src={require('public/images/pick-up.svg')} alt="" />
            <h6>{this.buildPickupLocation()}</h6>
            <span className={css.swapIcon}>
              <Icon
                className={classnames({ [css.swapIconDisabled]: swapDisabled })}
                type="swap"
                onClick={this.onClickSwapLocation}
              />
            </span>
          </div>
        </Col>
        <Col
          xs={24}
          md={hideServiceTypeSelect ? 10 : 7}
          className={css.pickupDropoffLocation}
          onClick={this.onClickDropoffLocation}
        >
          <div className={css.location}>
            <img src={require('public/images/drop-off.svg')} alt="" />
            <h6>{this.buildDropoffLocations()}</h6>
          </div>
        </Col>
        {!hideServiceTypeSelect && (
          <Col md={6} xs={24} className={css.serviceTypeSelect}>
            <Select
              value={selectedServiceTypeID}
              onChange={this.onChangeServiceType}
              dropdownClassName={selectFieldCss.selectDropdown}
            >
              {serviceTypes &&
                serviceTypes.map(s => {
                  const disableOption = s.maxMSDStops < currentStops;
                  const serviceTypeID = s.ID.toString();
                  return (
                    <SelectOption
                      key={serviceTypeID}
                      value={serviceTypeID}
                      className={selectFieldCss.selectOption}
                      disabled={disableOption}
                    >
                      <div>
                        <span>{s.name}</span>
                        {disableOption && (
                          <span className={selectFieldCss.disableMessage}>
                            <br />
                            {I18n.t(
                              'v2.serviceTypeSelect.disabledMessage.stopExceeded',
                              {
                                maxMSDStops: s.maxMSDStops,
                              },
                            )}
                          </span>
                        )}
                      </div>
                    </SelectOption>
                  );
                })}
            </Select>
          </Col>
        )}
        <Col md={4} className={css.reviewOrderColumn}>
          <Button
            className={css.reviewOrderButton}
            type="primary"
            onClick={onClickReviewOrder}
            disabled={disableReviewButton}
          >
            <div className={css.childWrap}>
              <div>
                <h6>
                  {I18n.t('v2.homepage.manualOrder.button.addOrderDetail')}
                </h6>
                <h5>{displayPrice}</h5>
              </div>
              <Icon type="right" />
            </div>
          </Button>
        </Col>
        <RouteOptimizationLocationDrawer
          key={locationDrawerID}
          onUpdate={onUpdateLocation}
          allowMultipleAddresses={allowMultipleAddresses}
          order={order}
          maxMSDStops={maxMSDStops}
        />
        <Mobile>
          <Drawer
            placement="bottom"
            width="100%"
            className={css.mobilePriceDrawer}
            visible={!_isEmpty(quoteForRender) && !locationDrawer.opening}
            height={120}
            closable={false}
            bodyStyle={{ padding: '8px 16px' }}
            mask={false}
          >
            <div className={css.mobilePrice}>
              <span>{I18n.t('booking.totalPrice')}</span>
              <h2> {displayPrice}</h2>
            </div>
            <Button
              className=""
              type="primary"
              onClick={onClickReviewOrder}
              disabled={disableReviewButton}
            >
              <span>
                {I18n.t('v2.homepage.manualOrder.button.addOrderDetail')}
              </span>
            </Button>
          </Drawer>
        </Mobile>
      </Row>
    );
  }
}

BookOrderWidget.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  order: PropTypes.shape({}),
  serviceTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updateOrder: PropTypes.func.isRequired,
  onClickReviewOrder: PropTypes.func.isRequired,
  toggleLocationDrawer: PropTypes.func.isRequired,
  locationDrawer: PropTypes.shape({
    opening: PropTypes.bool.isRequired,
    locations: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

BookOrderWidget.defaultProps = {
  order: {},
  orders: [],
};

export default BookOrderWidget;
